import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Compact "Back" button rendered at the top of every dashboard sub-page.
 *
 * Uses browser history (navigate(-1)) so users land exactly where they came
 * from — e.g. jumping from Properties → Calendar and back to Properties in
 * one click, instead of hunting through the sidebar.
 *
 * If we can detect this is the first entry in the tab's history (deep-link,
 * fresh tab, refresh), we fall back to the dashboard home so we never leave
 * users stranded on a browser-back that exits the app.
 */
const VIEW_LABELS = {
  calendar: 'Calendar',
  'calendar-sync': 'Calendar Sync',
  properties: 'Properties',
  files: 'Files',
  bookings: 'Bookings',
  cleaning: 'Cleaning',
  maintenance: 'Maintenance',
  renovations: 'Renovations',
  expenses: 'Expenses',
  workers: 'Workers',
  reports: 'Reports',
  invoices: 'Invoices',
  users: 'Team',
  billing: 'Billing',
  account: 'My Account',
  'team-chat': 'Team Chat',
  waitlist: 'Waitlist',
  analytics: 'Analytics',
};

export default function BackButton({ activeView }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Never show on the dashboard home — there is nothing to go back to.
  if (activeView === 'home') return null;

  const handleBack = () => {
    // history.state.idx is set by react-router; 0 = first entry in this tab.
    // If there's no prior in-app history, land on home instead of exiting.
    const idx = window.history.state && window.history.state.idx;
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const label = VIEW_LABELS[activeView] || 'Back';

  return (
    <div className="mb-4 flex items-center">
      <button
        type="button"
        onClick={handleBack}
        data-testid="dashboard-back-button"
        className="group inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 shadow-sm transition-colors hover:border-[#C9A44C] hover:text-[#0B1E3F]"
        aria-label={`Back from ${label}`}
      >
        <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
        Back
      </button>
      <span
        className="ml-3 text-xs uppercase tracking-wider text-slate-400"
        data-testid="dashboard-back-current-view"
      >
        {label}
      </span>
    </div>
  );
}
